import { KIND_LABEL, KINDS, MODULE_LABEL, type ClientModule } from "../../../server/rules/clients";
import { api, el } from "../../ui/dom";
import { openModal } from "../../ui/modal";

// Small form parts shared by the client screens (brief 1B-1, mockup .form, .field, .seg label).

let seq = 0;
const uid = (p: string) => `${p}${++seq}`;

/** A labeled text field (mockup .field). */
export function field(
  label: string,
  value: string | null | undefined,
  attrs: Record<string, string> = {},
): { el: HTMLElement; input: HTMLInputElement } {
  const id = uid("f");
  const input = el("input", {
    type: "text",
    id,
    value: value ?? "",
    autocomplete: "off",
    ...attrs,
  });
  return { el: el("div", { class: "field" }, el("label", { for: id }, label), input), input };
}

/** A labeled select. */
export function select(
  label: string,
  options: readonly (readonly [string, string])[],
  value: string | null | undefined,
): { el: HTMLElement; input: HTMLSelectElement } {
  const id = uid("s");
  const input = el(
    "select",
    { id },
    ...options.map(([v, text]) => el("option", { value: v, selected: v === (value ?? "") }, text)),
  );
  return { el: el("div", { class: "field" }, el("label", { for: id }, label), input), input };
}

/** A labeled text area. */
export function area(
  label: string,
  value: string | null | undefined,
): { el: HTMLElement; input: HTMLTextAreaElement } {
  const id = uid("t");
  const input = el("textarea", { id, rows: "3" }, value ?? "");
  return { el: el("div", { class: "field wide" }, el("label", { for: id }, label), input), input };
}

/** Radio or checkbox choices as pills (mockup .seg label), in a fieldset with its legend. */
export function pills(
  legend: string,
  type: "radio" | "checkbox",
  options: readonly (readonly [string, string])[],
  chosen: readonly string[],
  hint?: string,
): { el: HTMLElement; values: () => string[]; inputs: HTMLInputElement[] } {
  const name = uid("p");
  const inputs = options.map(([v]) =>
    el("input", { type, name, value: v, checked: chosen.includes(v) }),
  );
  const set = el(
    "fieldset",
    { class: "pills" },
    el("legend", {}, legend),
    ...options.map(([, text], i) => el("label", {}, inputs[i] as HTMLInputElement, text)),
  );
  return {
    el: el("div", { class: "field wide" }, set, hint ? el("span", { class: "hint" }, hint) : null),
    values: () => inputs.filter((i) => i.checked).map((i) => i.value),
    inputs,
  };
}

/** The client's details form, for New client and Edit: name, kind, type, modules, address. */
export function clientForm(
  module: ClientModule,
  modulesOn: readonly ClientModule[],
  start: {
    name?: string;
    kind?: string;
    clientType?: string | null;
    modules?: readonly string[];
    address?: Record<string, string | null>;
    /** Home Care's types are the company's own (Settings › Home care client types, brief 1C-1). */
    homecareTypes?: readonly { id: string; name: string }[];
    homecareTypeId?: string | null;
    /** Staffing's and Transportation's types are the company's own rows (brief 1D-6). */
    types?: readonly string[];
  } = {},
) {
  const name = field("Client name", start.name ?? "", { maxlength: "120" });
  const kind = select(
    "Kind",
    KINDS.map((k) => [k, KIND_LABEL[k]] as const),
    start.kind ?? "client",
  );
  // The module's own types, and the one the client has if it came from another module's list.
  // Home Care's are the company's client types, by id.
  const own = module === "homecare" && start.homecareTypes;
  const types = own ? [] : [...(start.types ?? [])];
  if (!own && start.clientType && !types.includes(start.clientType)) types.push(start.clientType);
  const type = pills(
    "Client type",
    "radio",
    own
      ? (start.homecareTypes ?? []).map((t) => [t.id, t.name] as const)
      : types.map((t) => [t, t] as const),
    own
      ? start.homecareTypeId
        ? [start.homecareTypeId]
        : []
      : start.clientType
        ? [start.clientType]
        : [],
    own
      ? "The type pre-fills intake, payer details and care rules from Settings › Home care client types."
      : undefined,
  );
  const used = pills(
    "Used in",
    "checkbox",
    modulesOn.map((m) => [m, MODULE_LABEL[m]] as const),
    start.modules ?? [module],
    "A client used by more than one module is one record in each list.",
  );
  const a = start.address ?? {};
  const line1 = field("Street", a.addressLine1, {
    maxlength: "120",
    autocomplete: "address-line1",
  });
  const line2 = field("Suite, floor", a.addressLine2, { maxlength: "120" });
  const city = field("City", a.city, { maxlength: "120" });
  const state = field("State", a.state, { maxlength: "2", inputmode: "text" });
  const zip = field("ZIP", a.zip, { maxlength: "10", inputmode: "numeric" });
  const el_ = el(
    "div",
    { class: "formgrid" },
    el("div", { class: "wide" }, name.el),
    kind.el,
    el("div"),
    type.el,
    used.el,
    el("div", { class: "wide eyebrow", style: "margin-top:4px" }, "Address"),
    el("div", { class: "wide" }, line1.el),
    line2.el,
    city.el,
    state.el,
    zip.el,
  );
  return {
    el: el_,
    focus: () => name.input.focus(),
    inputs: [
      name.input,
      kind.input,
      ...type.inputs,
      ...used.inputs,
      line1.input,
      line2.input,
      city.input,
      state.input,
      zip.input,
    ],
    values: () => ({
      name: name.input.value,
      kind: kind.input.value,
      ...(own
        ? { homecareTypeId: type.values()[0] ?? null }
        : { clientType: type.values()[0] ?? null }),
      modules: used.values(),
      addressLine1: line1.input.value,
      addressLine2: line2.input.value,
      city: city.input.value,
      state: state.input.value,
      zip: zip.input.value,
    }),
  };
}

/** Shows a refusal at the top of a modal in plain words. */
export function refuse(
  note: HTMLElement,
  error: string | undefined,
  /** A record the refusal names, opened from the note (brief 1E-30: whose record has it). */
  open?: { href: string | null | undefined; label?: string | null } | null,
) {
  note.hidden = false;
  note.textContent = error ?? "That wasn't saved. Check the connection and try again.";
  if (open?.href) {
    const a = el("a", { href: open.href, "data-open-record": "" }, open.label ?? "Open");
    note.append(" ", a);
  }
}

/**
 * Asks why something is archived, then archives it. Resolves true when it was archived.
 */
export function archiveModal(what: string, path: string, onDone: () => unknown) {
  const reason = area("Why is it archived?", "");
  const go = el("button", { type: "button", class: "btn pri" }, "Archive");
  const m = openModal({
    title: `Archive ${what}`,
    sub: "It moves to Archived with the reason given.",
    body: [el("div", { class: "form", style: "max-width:none" }, reason.el)],
    actions: [go],
  });
  reason.input.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const res = await api("POST", path, { reason: reason.input.value });
    go.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    await onDone();
  });
}

/** The contract's end, with its warning chip when it ends within 60 days or has ended. */
export function endsCell(
  ends: string | null,
  warning: { level: string; text: string } | null,
): HTMLElement {
  return el(
    "span",
    {},
    ends ?? "–",
    warning
      ? el("span", {}, " ", el("span", { class: `chip ${warning.level}` }, warning.text))
      : null,
  );
}
